// src/pages/admin/blogs/AdminBlogModal.jsx
import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Switch,
  InputNumber,
  Space,
  Tag,
  Descriptions,
  Image,
  Typography,
  Button,
  message,
  Progress,
} from "antd";
import {
  ClockCircleOutlined,
  EyeOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import axios from "../../../requests/axios";
import routes from "../../../requests/routes";
import RichText from "../RichTextEditor/RichTextEditor";

// your generic pickers
import {
  ImageUploadMulti,
  ImageUploadSingle,
  normFile,
} from "../../../generic components/Uploads/ImageUploads";

// your utils (must upload a single file and return a URL; uploadMany returns string[] of URLs)
import { uploadMany, uploadOne } from "../../../utils/utils";

const { Text, Title, Paragraph } = Typography;

function statusTag(isPublished) {
  if (isPublished === true) return <Tag color="green">Approved</Tag>;
  if (isPublished === false) return <Tag color="red">Declined</Tag>;
  return <Tag icon={<ClockCircleOutlined />}>Pending</Tag>;
}

const getRaw = (f) => (f?.originFileObj ? f.originFileObj : f);

export default function AdminBlogModal({ open, blog, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  // progress
  const [coverPct, setCoverPct] = useState(0);
  const [galleryOverallPct, setGalleryOverallPct] = useState(0);
  const [galleryFilePct, setGalleryFilePct] = useState({}); // { uid: percent }

  // double-submit guard (more reliable than state during rapid clicks)
  const submittingRef = useRef(false);

  useEffect(() => {
    if (blog && open) {
      const coverList = blog.coverImage
        ? [
            {
              uid: "cover-1",
              name: blog.coverImage.split("/").pop() || "cover",
              status: "done",
              url: blog.coverImage,
            },
          ]
        : [];

      const galleryList = Array.isArray(blog.gallery)
        ? blog.gallery.map((url, i) => ({
            uid: `g-${i}`,
            name: url.split("/").pop() || `image-${i + 1}`,
            status: "done",
            url,
          }))
        : [];

      form.setFieldsValue({
        title: blog.title || "",
        summary: blog.summary || "",
        contentText: blog.contentText || "",
        tags: Array.isArray(blog.tags) ? blog.tags : [],
        coverImage: coverList,
        gallery: galleryList,
        isHomepage: !!blog.isHomepage,
        isFeatured: !!blog.isFeatured,
        isPublished: !!blog.isPublished,
        order: blog.order ?? null,
      });
    } else {
      form.resetFields();
    }
    // reset progress visuals whenever modal opens/closes or blog changes
    setCoverPct(0);
    setGalleryOverallPct(0);
    setGalleryFilePct({});
  }, [blog, open, form]);

  const handleSave = async (values) => {
    if (!blog?._id) return;
    if (submittingRef.current) return; // guard
    submittingRef.current = true;

    try {
      setSaving(true);

      // ---- COVER (upload only if new file selected) ----
      let coverImageUrl = blog.coverImage || "";
      const coverList = values.coverImage || [];
      const coverFile = coverList[0];

      if (coverFile) {
        if (coverFile.url) {
          // existing image kept
          coverImageUrl = coverFile.url;
        } else if (coverFile.originFileObj) {
          // new file picked
          coverImageUrl = await uploadOne(getRaw(coverFile), setCoverPct);
          setCoverPct(100);
        }
      } else {
        // user removed the cover
        coverImageUrl = "";
      }

      // ---- GALLERY (only upload new files; keep URLs for existing; removed ones are dropped) ----
      const galleryList = values.gallery || [];

      const existingUrls = galleryList.filter((f) => !!f.url).map((f) => f.url);

      const newFiles = galleryList.filter((f) => !f.url && !!f.originFileObj);

      let uploadedUrls = [];
      if (newFiles.length > 0) {
        // hook per-file and overall progress
        uploadedUrls = await uploadMany(
          newFiles,
          // per-file
          (uid, pct) =>
            setGalleryFilePct((m) => ({
              ...m,
              [uid]: pct,
            })),
          // overall (number 0..100)
          (overallPct) => setGalleryOverallPct(overallPct)
        );
      }
      const finalGallery = [...existingUrls, ...uploadedUrls];

      // ---- PAYLOAD ----
      const payload = {
        title: values.title,
        summary: values.summary || "",
        contentText: values.contentText,
        coverImage: coverImageUrl,
        gallery: finalGallery, // only strings
        tags: Array.isArray(values.tags)
          ? values.tags
          : String(values.tags || "")
              .split(",")
              .map((t) => t.trim())
              .filter(Boolean),
        isHomepage: !!values.isHomepage,
        isFeatured: !!values.isFeatured,
        isPublished: !!values.isPublished,
        order: values.order ?? null,
      };

      // ---- SAVE ----
      const { data } = await axios.patch(
        `${routes.getAdminBlogs}/${blog._id}`,
        payload
      );

      const updated = data?.blog || data?.item || data;
      message.success("Blog saved");
      onSaved && onSaved(updated || { ...blog, ...payload });
      onClose && onClose();
    } catch (e) {
      message.error(e?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
      submittingRef.current = false;
      // small UX cleanup
      setCoverPct(0);
      setGalleryOverallPct(0);
      setGalleryFilePct({});
    }
  };

  const footer = (
    <Space>
      <Button onClick={onClose}>Close</Button>
      <Button
        type="primary"
        icon={<UploadOutlined />}
        onClick={() => form.submit()}
        loading={saving}
      >
        Save
      </Button>
    </Space>
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={footer}
      width={900}
      destroyOnClose
      title={
        <Space align="center">
          <EyeOutlined />
          <span>{blog ? `Blog: ${blog.title || "Untitled"}` : "Blog"}</span>
          {statusTag(blog?.isPublished)}
        </Space>
      }
    >
      {/* QUICK PREVIEW */}
      <Space
        align="start"
        size={24}
        style={{ width: "100%", marginBottom: 16 }}
        wrap
      >
        <div style={{ flex: 1, minWidth: 260 }}>
          <Descriptions
            column={2}
            size="small"
            style={{ marginTop: 8 }}
            items={[
              {
                key: "status",
                label: "Status",
                children: statusTag(blog?.isPublished),
                span: 1,
              },
              {
                key: "publishDate",
                label: "Publish Date",
                children: blog?.publishDate
                  ? new Date(blog.publishDate).toLocaleString()
                  : "—",
                span: 1,
              },
              {
                key: "order",
                label: "Order",
                children: blog?.order > 0 ? blog.order : "—",
                span: 1,
              },
              {
                key: "views",
                label: "Views",
                children: blog?.views ?? 0,
                span: 1,
              },
            ]}
          />
        </div>
      </Space>

      {/* EDIT FORM */}
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        initialValues={{
          title: "",
          summary: "",
          contentText: "",
          tags: [],
          coverImage: [],
          gallery: [],
          isHomepage: false,
          isFeatured: false,
          isPublished: false,
          order: null,
        }}
      >
        <Form.Item
          label="Title"
          name="title"
          rules={[{ required: true, message: "Title is required" }]}
        >
          <Input placeholder="Blog title" />
        </Form.Item>

        <Form.Item label="Summary" name="summary">
          <Input.TextArea rows={3} placeholder="Short summary" />
        </Form.Item>

        <Form.Item
          label="Content"
          name="contentText"
          rules={[
            { required: true, message: "Content is required" },
            {
              validator: (_, v) => {
                const text = String(v || "")
                  .replace(/<[^>]+>/g, "")
                  .trim();
                return text.length >= 10
                  ? Promise.resolve()
                  : Promise.reject("Minimum 10 characters");
              },
            },
          ]}
        >
          {RichText ? (
            <RichText
              value={form.getFieldValue("contentText")}
              onChange={(html) => form.setFieldsValue({ contentText: html })}
            />
          ) : (
            <Input.TextArea rows={6} placeholder="Write your content…" />
          )}
        </Form.Item>

        <Space style={{ gap: 12 }} wrap>
          <Form.Item
            label="Tags"
            name="tags"
            style={{ flex: 1, minWidth: 260 }}
          >
            <Select
              mode="tags"
              placeholder="Type and press Enter to add tags"
            />
          </Form.Item>
          <Form.Item
            label="Order (manual)"
            name="order"
            style={{ marginBottom: 0 }}
          >
            <InputNumber min={1} placeholder="Leave empty to keep current" />
          </Form.Item>
        </Space>

        <Space style={{ gap: 12 }} wrap>
          <Form.Item
            label="Cover Image"
            name="coverImage"
            valuePropName="fileList"
            getValueFromEvent={normFile}
            style={{ flex: 1, minWidth: 260 }}
          >
            <ImageUploadSingle />
          </Form.Item>

          <Form.Item
            label="Homepage"
            name="isHomepage"
            valuePropName="checked"
            style={{ marginBottom: 0 }}
          >
            <Switch />
          </Form.Item>
          <Form.Item
            label="Featured"
            name="isFeatured"
            valuePropName="checked"
            style={{ marginBottom: 0 }}
          >
            <Switch />
          </Form.Item>
          <Form.Item
            label="Publish"
            name="isPublished"
            valuePropName="checked"
            style={{ marginBottom: 0 }}
          >
            <Switch />
          </Form.Item>
        </Space>

        <Form.Item
          label="Gallery"
          name="gallery"
          valuePropName="fileList"
          getValueFromEvent={normFile}
        >
          <ImageUploadMulti />
        </Form.Item>

        {/* Save-time progress (only visible while saving) */}
        {saving && (
          <div style={{ marginTop: 8 }}>
            {!!form.getFieldValue("coverImage")?.length && (
              <>
                <div style={{ fontSize: 12, opacity: 0.75 }}>Cover upload</div>
                <Progress percent={coverPct} size="small" />
              </>
            )}
            {(form.getFieldValue("gallery") || []).length > 0 && (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 12, opacity: 0.75 }}>
                  Gallery overall
                </div>
                <Progress percent={galleryOverallPct} />
                {/* Optional per-file breakdown for new files
                {(form.getFieldValue("gallery") || [])
                  .filter((f) => !f.url) // only new files show bars
                  .map((f) => (
                    <div key={f.uid} style={{ marginTop: 6 }}>
                      <div style={{ fontSize: 12, opacity: 0.75 }}>
                        {f.name}
                      </div>
                      <Progress
                        percent={galleryFilePct[f.uid] || 0}
                        size="small"
                      />
                    </div>
                  ))} */}
              </div>
            )}
          </div>
        )}
      </Form>
    </Modal>
  );
}
